import { MouseEvent, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  webcamsFeed,
  webcamsInformations,
  webcamsInformations_webcams,
} from "../../Api/types";
import { api_postValueOrErrorMessage } from "../../Api/util";
import usePlatform from "../../Hook/platform";
import { useResolvedMode } from "../../Hook/theme";
import { NAVIGATE } from "../../Router";
import Loader from "../Loader";
import { AdsBannerProps } from "./config";
import AdTile from "./AdTile";

type WebcamPreview = {
  name?: string;
  thumbnail: string;
  viewers: number;
};

const FALLBACK_PREVIEW: WebcamPreview[] = [
  { thumbnail: "/logo192.png", viewers: 812 },
  { thumbnail: "/logo192.png", viewers: 654 },
  { thumbnail: "/logo192.png", viewers: 529 },
];

const asPreview = (cam: webcamsInformations_webcams): WebcamPreview | null => {
  if (!cam.thumbnail) return null;
  return {
    name: cam.name,
    thumbnail: cam.thumbnail,
    viewers: cam.nb_connected || 0,
  };
};

/**
 * Family B, webcams (2d): the pale peach tile is the light pair as it ships;
 * dark inverts it — deep gradient, white copy — because a near-white tile is
 * the loudest thing on a dark feed, and not on purpose. The leaderboard takes
 * two previews, not three: the kicker and the CTA are unshrinkable at 728.
 */
const MODES = {
  light: {
    tile: "border border-[#f4c0ac] bg-gradient-to-br from-[#fff4ed] via-[#ffe7da] to-[#ffd9c7] shadow-[0_16px_36px_rgba(164,84,52,0.22)]",
    kicker: "text-[#934b2f]",
    cta: "bg-[#5f2b19] text-white",
    caption: "bg-[#fff6f1] text-[#6e3320]",
    dismissLight: true,
  },
  dark: {
    tile: "border border-[#6f3520] bg-gradient-to-br from-[#4a2313] via-[#5f2b19] to-[#6f3520] shadow-[0_16px_36px_rgba(31,14,7,0.5)]",
    kicker: "text-[#f5c9b2]",
    cta: "bg-[#ffe7da] text-[#5f2b19]",
    caption: "bg-[#3a1b0e] text-[#f5c9b2]",
    dismissLight: false,
  },
};

function WebcamBannerNative({ onDismiss }: AdsBannerProps) {
  const [preview, setPreview] = useState<WebcamPreview[]>([]);
  const platform = usePlatform();
  const tones = MODES[useResolvedMode()];
  const providerCodes = (
    platform?.platform?.highlighted_webcams_providers || []
  ).map((provider) => provider.provider);
  const providerSignature = providerCodes.join("|");

  const openWebcamsPage = () => {
    if (NAVIGATE.navigate) NAVIGATE.navigate("/webcams");
  };

  const openCam = (event: MouseEvent) => {
    event.stopPropagation();
    openWebcamsPage();
  };

  useEffect(() => {
    // BC5: before the platform payload lands the provider list is empty and the
    // whole chain would run once for nothing, then again for real.
    if (!platform?.platform) return;
    let cancelled = false;

    (async () => {
      try {
        const defaultSearch = api_postValueOrErrorMessage<webcamsFeed>(
          "webcams/search",
          {
            limit: 1,
            offset: 0,
            genders: ["woman"],
            statutes: ["public"],
            sort: "nb_connected",
            qualities: ["sd", "hd"],
          },
        );

        const idsByProvider = await Promise.all(
          providerCodes.map((provider) =>
            api_postValueOrErrorMessage<webcamsFeed>("webcams/highlighted", {
              limit: 1,
              offset: 0,
              provider,
              genders: ["woman", "couple", "trans"],
              statutes: ["public"],
            }),
          ),
        );

        const defaultIds = await defaultSearch;

        const webcamIds = [idsByProvider[0], defaultIds, idsByProvider[1]]
          .map((feed) => feed.webcams?.[0])
          .filter((id): id is string => Boolean(id));

        const uniqueWebcamIds = webcamIds.filter(
          (id, index, ids) => ids.indexOf(id) === index,
        );

        if (!uniqueWebcamIds.length) {
          throw new Error("No webcams found");
        }

        const info = await api_postValueOrErrorMessage<webcamsInformations>(
          "webcams/informations",
          { webcams: uniqueWebcamIds },
        );

        const infosById = new Map(
          (info.webcams || []).map((cam) => [cam.id, cam]),
        );

        const withThumb = uniqueWebcamIds
          .map((id) => infosById.get(id))
          .filter((cam): cam is webcamsInformations_webcams => Boolean(cam))
          .map(asPreview)
          .filter((cam): cam is WebcamPreview => Boolean(cam));

        const selected = withThumb.slice(0, 3);

        if (!cancelled) {
          setPreview(selected.length ? selected : FALLBACK_PREVIEW);
        }
      } catch {
        if (!cancelled) {
          setPreview(FALLBACK_PREVIEW);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [providerSignature, !platform?.platform]);

  const previewItems = preview.length ? preview : FALLBACK_PREVIEW;
  const showFallbackPreview = previewItems === FALLBACK_PREVIEW;

  return (
    <AdTile
      testId="ad-webcam"
      onOpen={openWebcamsPage}
      onDismiss={onDismiss}
      className={tones.tile}
      native
      dismissLight={tones.dismissLight}
    >
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
        <p className={"text-xs sm:text-base font-semibold uppercase tracking-[0.24em] truncate " + tones.kicker}>
          <FormattedMessage
            id="ads_webcam_native_kicker"
            defaultMessage="Cam shows en direct"
          />
        </p>
        <span
          className={
            "self-start inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] sm:text-xs font-bold whitespace-nowrap " +
            tones.cta
          }
        >
          <FormattedMessage
            id="ads_webcam_native_cta"
            defaultMessage="Voir nos cams en ligne"
          />
          <span aria-hidden>→</span>
        </span>
      </div>

      {/* Two previews on the wide slot, three never fit past the CTA. The phone
          slot keeps two as well — at 300px a third is a sliver. */}
      <div className="flex-none flex gap-2 h-[72px] sm:h-[66px]">
        {previewItems.slice(0, 2).map((cam, index) => (
          <button
            type="button"
            key={`${cam.name}-${index}`}
            onClick={openCam}
            className="relative w-[84px] sm:w-[104px] overflow-hidden rounded-lg border border-black/10 bg-black/20 text-left"
          >
            {showFallbackPreview ? (
              <div className="flex h-full w-full items-center justify-center bg-black/30 text-white">
                <Loader size="inline" tone="current" />
              </div>
            ) : (
              <img
                className="h-full w-full object-cover"
                src={cam.thumbnail}
                alt={cam.name || "webcam"}
                loading="lazy"
              />
            )}
            {Boolean(cam.viewers) && (
              <span
                className={
                  "absolute bottom-0 inset-x-0 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold " +
                  tones.caption
                }
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#4BD963]" aria-hidden />
                Live · {cam.viewers}+
              </span>
            )}
          </button>
        ))}
      </div>
    </AdTile>
  );
}

export default WebcamBannerNative;
